/**
 * 商談詳細ページ — 業務ハブ (Phase A / P-D 再構築)。
 *
 * 構成:
 *  - DealHeader: 顧客名 / 金額 / ステージ・ステータス
 *  - NextActionBar: 「次のアクション」(useNextActions が状態から導出)
 *  - 5 タブ: 概要 / 個別説明 / 見積 / 請求・発注・発送 / 顧客
 *
 * 1 商談の全工程をこの 1 画面で完結させ、商談ボード → 各管理画面 の渡り歩きを廃止する。
 */
import { Briefcase, FileText, Receipt, ShieldCheck, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { DealHeader } from './components/DealHeader';
import { DealStageStepper } from './components/DealStageStepper';
import { NextActionBar } from './components/NextActionBar';
import { DealBriefingTab } from './tabs/DealBriefingTab';
import { DealConsentTab } from './tabs/DealConsentTab';
import { DealContactTab } from './tabs/DealContactTab';
import { DealFulfillmentTab } from './tabs/DealFulfillmentTab';
import { DealOverviewTab } from './tabs/DealOverviewTab';
import { DealQuotesTab } from './tabs/DealQuotesTab';

import { BackToPreviousButton } from '@/components/common/BackToPreviousButton';
import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDealConsentRequirement } from '@/hooks/useConsentDocuments';
import { useDeal } from '@/hooks/useDeals';
import { useMailContact } from '@/hooks/useMailContacts';
import { useNextActions, type DealTabKey } from '@/hooks/useNextActions';

const TAB_KEYS: readonly DealTabKey[] = [
  'overview',
  'briefing',
  'quotes',
  'fulfillment',
  'consent',
  'contact',
];

function parseTab(v: string | null): DealTabKey | null {
  return v && (TAB_KEYS as readonly string[]).includes(v) ? (v as DealTabKey) : null;
}

export function DealDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: deal, isLoading } = useDeal(id);
  const { data: contact } = useMailContact(deal?.contactId);
  const { actions, badgeCounts } = useNextActions(deal);

  const [searchParams, setSearchParams] = useSearchParams();
  // 入口から渡された「前の画面へ」の戻り先 (location.state)。タブ切替で URL を書き換えても消えないよう引き継ぐ
  const { state: locationState } = useLocation();
  const [tab, setTab] = useState<DealTabKey>('overview');
  // 発注一覧・送金依頼・次のアクションから開いた時に、該当の発注カードまでスクロールして強調する
  const [focus, setFocus] = useState<{ poId: string; nonce: number } | null>(null);

  // URL の ?tab= / ?po= に追従 (例: /deals/:id?tab=fulfillment&po=<poId>)
  const urlTab = searchParams.get('tab');
  const urlPo = searchParams.get('po');
  useEffect(() => {
    const t = parseTab(urlTab);
    if (t) setTab(t);
    if (urlPo) setFocus({ poId: urlPo, nonce: Date.now() });
  }, [id, urlTab, urlPo]);

  const changeTab = (t: DealTabKey) => {
    setTab(t);
    setFocus(null);
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    next.delete('po');
    setSearchParams(next, { replace: true, state: locationState });
  };

  const jumpTo = (t: DealTabKey, poId?: string) => {
    changeTab(t);
    if (poId) setFocus({ poId, nonce: Date.now() });
  };

  // v3.2.25: 主軸カテゴリーで同意ステップが有効かどうか
  // 2026-10-16: 同じ主軸カテゴリーの別の商談でサイン済みの顧客は省略する
  const { requiresConsent } = useDealConsentRequirement(deal);

  // タブ表示時に label に件数バッジを付ける
  const tabsConfig = useMemo(() => {
    type TabConfig = {
      value: DealTabKey;
      label: string;
      icon: typeof FileText | null;
    };
    const base: TabConfig[] = [
      { value: 'overview', label: '概要', icon: null },
      { value: 'briefing', label: '個別説明', icon: null },
      { value: 'quotes', label: '見積', icon: FileText },
      { value: 'fulfillment', label: '請求・発注・発送', icon: Receipt },
    ];
    if (requiresConsent) {
      base.push({ value: 'consent', label: '同意書', icon: ShieldCheck });
    }
    base.push({ value: 'contact', label: '顧客', icon: User });
    return base;
  }, [requiresConsent]);

  if (isLoading) {
    return (
      <Screen title="商談詳細">
        <Spinner />
      </Screen>
    );
  }

  if (!deal) {
    return (
      <Screen title="商談詳細">
        <div className="mb-2">
          <BackToPreviousButton />
        </div>
        <EmptyState
          icon={Briefcase}
          title="商談が見つかりません"
          description="削除されたか、URL が誤っている可能性があります。"
        />
      </Screen>
    );
  }

  return (
    <Screen title="商談詳細">
      <DealHeader deal={deal} contact={contact ?? null} />

      {/* 全工程のどこにいるかを 1 本で見せる (2026-07-29) */}
      <DealStageStepper deal={deal} requiresConsent={requiresConsent} />

      <NextActionBar actions={actions} onJumpTab={jumpTo} />

      <Tabs value={tab} onValueChange={(v) => changeTab(v as DealTabKey)}>
        <TabsList className="overflow-x-auto flex-nowrap [&>*]:flex-shrink-0">
          {tabsConfig.map((t) => {
            const count = badgeCounts[t.value];
            return (
              <TabsTrigger key={t.value} value={t.value}>
                {t.icon && <t.icon className="size-3.5 mr-1" />}
                {t.label}
                {count > 0 && (
                  <Badge variant="danger" className="ml-1.5">
                    {count}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="overview">
          <DealOverviewTab deal={deal} />
        </TabsContent>
        <TabsContent value="briefing">
          <DealBriefingTab
            deal={deal}
            recipientEmail={contact?.defaultEmail ?? null}
            contactName={contact?.name ?? null}
          />
        </TabsContent>
        <TabsContent value="quotes">
          <DealQuotesTab
            deal={deal}
            recipientEmail={contact?.defaultEmail ?? null}
            onJumpFulfillment={() => changeTab('fulfillment')}
          />
        </TabsContent>
        <TabsContent value="fulfillment">
          <DealFulfillmentTab
            deal={deal}
            focusPoId={focus?.poId ?? null}
            focusNonce={focus?.nonce}
          />
        </TabsContent>
        {requiresConsent && (
          <TabsContent value="consent">
            <DealConsentTab deal={deal} />
          </TabsContent>
        )}
        <TabsContent value="contact">
          <DealContactTab contact={contact ?? null} />
        </TabsContent>
      </Tabs>
    </Screen>
  );
}
